/**
 * Soft empty / dead-end state when the applicant lands without a valid session
 * link. Not a verification failure — use info tone (not red ✕) and recovery
 * guidance instead of jargon like “session token”.
 */
import { useTranslation } from 'react-i18next';

export type EmptySessionKind = 'home' | 'missing' | 'handoff';

interface EmptySessionProps {
  kind?: EmptySessionKind;
  /** Optional support mailto / help URL. */
  supportHref?: string;
}

const SUPPORT_HREF =
  import.meta.env.FRONTEND_SUPPORT_URL?.trim() || 'mailto:support@trustsy.com';

export function EmptySession({ kind = 'missing', supportHref = SUPPORT_HREF }: EmptySessionProps) {
  const { t } = useTranslation();
  const prefix = `empty.${kind}`;

  return (
    <main className="screen screen--empty">
      <div className="card card--empty enter" data-testid="empty-session">
        <div className="empty-icon" aria-hidden="true">
          <LinkIcon />
        </div>

        <h1 className="empty-title">
          {t(`${prefix}.title`, {
            defaultValue: defaultTitle(kind),
          })}
        </h1>
        <p className="empty-body">
          {t(`${prefix}.body`, {
            defaultValue: defaultBody(kind),
          })}
        </p>

        <ul className="empty-tips">
          <li>
            {t('empty.tipEmail', {
              defaultValue: 'Open the original link from your email or SMS',
            })}
          </li>
          <li>
            {t('empty.tipQr', {
              defaultValue: 'If you scanned a QR, go back and scan it again',
            })}
          </li>
          <li>
            {t('empty.tipOrg', {
              defaultValue: 'Ask the organization that invited you for a new link',
            })}
          </li>
        </ul>

        <div className="empty-actions">
          <a className="btn" href={supportHref}>
            {t('empty.support', { defaultValue: 'Contact support' })}
          </a>
        </div>

        <p className="empty-foot">
          {t('empty.secure', {
            defaultValue: 'Secure identity verification by trustsy',
          })}
        </p>
      </div>
    </main>
  );
}

function defaultTitle(kind: EmptySessionKind): string {
  switch (kind) {
    case 'home':
      return 'Identity verification';
    case 'handoff':
      return 'This handoff link is invalid';
    case 'missing':
    default:
      return 'Unable to start verification';
  }
}

function defaultBody(kind: EmptySessionKind): string {
  switch (kind) {
    case 'home':
      return 'To verify your identity, open the secure link you received by email or SMS. You can also scan the QR code shown on a desktop screen.';
    case 'handoff':
      return 'This phone handoff link is incomplete or has expired. Return to the desktop page and scan a new QR code.';
    case 'missing':
    default:
      return 'This link is incomplete or has expired. Use the invitation from your email or SMS, or ask the organization that invited you for a new one.';
  }
}

function LinkIcon() {
  return (
    // 20px inside the 48px tile (`.empty-icon`). Sized here rather than in CSS
    // because the SVG carries no intrinsic ratio without explicit attributes.
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <path
        d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
